import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { AiUsageOverview } from './AiUsageOverview';
import { useQuery } from '@tanstack/react-query';
import { BarChart3, DollarSign, MessageSquare, Cpu, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { fetchOpusUsageSummary, fetchOpusUsageLogs } from '@/lib/api';

export function AiUsageDashboard() {
  const { user } = useAuth();
  return user?.role === 'admin' ? <div className="space-y-6"><AiUsageOverview /><details className="rounded-xl border border-white/10 p-4"><summary className="cursor-pointer font-medium">CRM per-user detail and usage log</summary><div className="mt-4"><CrmUsageDetails /></div></details></div> : <CrmUsageDetails />;
}

function CrmUsageDetails() {
  const [period, setPeriod] = useState(30);
  const [page, setPage] = useState(1);

  const { data: summary, isLoading: summaryLoading, isError: summaryError } = useQuery({
    queryKey: ['opus-ai-usage-summary', period],
    queryFn: () => fetchOpusUsageSummary(period),
  });

  const { data: logsData, isLoading: logsLoading, isError: logsError } = useQuery({
    queryKey: ['opus-ai-usage-logs', page],
    queryFn: () => fetchOpusUsageLogs(page, 15),
  });

  if (summaryLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-text-tertiary" />
      </div>
    );
  }

  if (summaryError) return <p role="alert" className="text-amber-400">CRM usage could not be loaded. Please retry.</p>;

  return (
    <div className="space-y-6">
      {/* Period Selector */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-text-secondary">Period:</span>
        {[7, 30, 90].map((d) => (
          <button
            key={d}
            onClick={() => setPeriod(d)}
            className={`px-3 py-1 text-sm rounded-lg transition-colors ${
              period === d
                ? 'bg-emerald-600 text-white'
                : 'bg-white/5 text-text-secondary hover:bg-white/10'
            }`}
          >
            {d}d
          </button>
        ))}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-900/30">
                <DollarSign className="h-5 w-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-xs text-text-tertiary">Recorded CRM Cost</p>
                <p className="text-xl font-semibold text-text-primary">
                  ${summary?.totalCostUsd.toFixed(4) ?? '0.00'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-900/30">
                <MessageSquare className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-xs text-text-tertiary">Usage entries</p>
                <p className="text-xl font-semibold text-text-primary">
                  {summary?.totalMessages ?? 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-900/30">
                <MessageSquare className="h-5 w-5 text-purple-500" />
              </div>
              <div>
                <p className="text-xs text-text-tertiary">Conversations</p>
                <p className="text-xl font-semibold text-text-primary">
                  {summary?.totalConversations ?? 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-900/30">
                <Cpu className="h-5 w-5 text-amber-500" />
              </div>
              <div>
                <p className="text-xs text-text-tertiary">Total Tokens</p>
                <p className="text-xl font-semibold text-text-primary">
                  {((summary?.totalInputTokens ?? 0) + (summary?.totalOutputTokens ?? 0)).toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Usage by User */}
      {summary?.byUser && summary.byUser.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Usage by User
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {summary.byUser.map((u) => {
                const pct = summary.totalCostUsd > 0
                  ? (u.totalCostUsd / summary.totalCostUsd) * 100
                  : 0;
                return (
                  <div key={u.userId} className="flex items-center gap-3">
                    <div className="w-32 text-sm text-text-primary truncate">
                      {u.firstName} {u.lastName}
                    </div>
                    <div className="flex-1 bg-white/5 rounded-full h-2">
                      <div
                        className="bg-emerald-500 h-2 rounded-full transition-all"
                        style={{ width: `${Math.max(pct, 2)}%` }}
                      />
                    </div>
                    <div className="w-20 text-right text-sm text-text-secondary">
                      ${u.totalCostUsd.toFixed(4)}
                    </div>
                    <div className="w-16 text-right text-xs text-text-tertiary">
                      {u.totalMessages} msgs
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Daily Cost Chart (simple bar representation) */}
      {summary?.byDay && summary.byDay.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Daily Cost</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-1 h-32">
              {summary.byDay.slice(-30).map((day) => {
                const maxCost = Math.max(...summary.byDay.map((d) => d.costUsd), 0.001);
                const height = (day.costUsd / maxCost) * 100;
                return (
                  <div
                    key={day.date}
                    className="flex-1 bg-emerald-600 rounded-t hover:bg-emerald-500 transition-colors group relative min-w-[4px]"
                    style={{ height: `${Math.max(height, 2)}%` }}
                    title={`${day.date}: $${day.costUsd.toFixed(4)} (${day.messages} msgs)`}
                  />
                );
              })}
            </div>
            <div className="flex justify-between mt-1 text-xs text-text-tertiary">
              <span>{summary.byDay[0]?.date}</span>
              <span>{summary.byDay[summary.byDay.length - 1]?.date}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Usage Logs */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent Usage Logs</CardTitle>
        </CardHeader>
        <CardContent>
          {logsLoading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-text-tertiary" />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-2 px-2 text-text-tertiary font-medium">User</th>
                      <th className="text-left py-2 px-2 text-text-tertiary font-medium">Model</th>
                      <th className="text-right py-2 px-2 text-text-tertiary font-medium">Tokens</th>
                      <th className="text-right py-2 px-2 text-text-tertiary font-medium">Cost</th>
                      <th className="text-right py-2 px-2 text-text-tertiary font-medium">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logsData?.data.map((log) => (
                      <tr key={log.id} className="border-b border-white/5">
                        <td className="py-2 px-2 text-text-primary">{log.userName}</td>
                        <td className="py-2 px-2 text-text-secondary text-xs">
                          {log.model.split('-').slice(0, 2).join('-')}
                        </td>
                        <td className="py-2 px-2 text-right text-text-secondary">
                          {(log.inputTokens + log.outputTokens).toLocaleString()}
                        </td>
                        <td className="py-2 px-2 text-right text-text-primary">
                          ${log.costUsd.toFixed(4)}
                        </td>
                        <td className="py-2 px-2 text-right text-text-tertiary text-xs">
                          {new Date(log.createdAt).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                    {(!logsData?.data || logsData.data.length === 0) && (
                      <tr>
                        <td colSpan={5} className="py-6 text-center text-text-tertiary">
                          {logsError ? 'Usage logs could not be loaded' : 'No usage logs yet'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {logsData?.pagination && logsData.pagination.totalPages > 1 && (
                <div className="flex justify-center gap-2 mt-4">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 text-sm rounded bg-white/5 text-text-secondary hover:bg-white/10 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-1 text-sm text-text-tertiary">
                    {page} / {logsData.pagination.totalPages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(logsData.pagination.totalPages, p + 1))}
                    disabled={page >= logsData.pagination.totalPages}
                    className="px-3 py-1 text-sm rounded bg-white/5 text-text-secondary hover:bg-white/10 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
